import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../api/client';
import { SnapshotViewerModal } from './SnapshotViewerModal';

interface UploadHistoryEntry {
  id: string;
  fileName: string;
  fileReferenceId: string;
  uploadDate: string;
  guestCount: number;
  orderCount: number;
  duplicateCount: number;
  status: 'Pending' | 'Confirmed' | 'Cancelled' | 'Abandoned' | 'Failed' | 'RolledBack';
  errorMessage: string | null;
  uploadedBy: { fullName: string } | null;
  hasSnapshots: boolean;
}

interface HistoryResponse {
  entries: UploadHistoryEntry[];
  total: number;
  page: number;
  pageSize: number;
}

interface UploadHistoryProps {
  refreshTrigger: number;
}

const PAGE_SIZE = 20;

const statusColors: Record<string, { bg: string; text: string }> = {
  Confirmed: { bg: '#dcfce7', text: '#166534' },
  Pending: { bg: '#fef9c3', text: '#854d0e' },
  Cancelled: { bg: '#f3f4f6', text: '#4b5563' },
  Abandoned: { bg: '#ffedd5', text: '#9a3412' },
  Failed: { bg: '#fdecea', text: '#991b1b' },
  RolledBack: { bg: '#ede9fe', text: '#5b21b6' },
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: '1.5rem',
  },
  heading: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '0.75rem',
    marginTop: 0,
  },
  tableWrapper: {
    overflowX: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.8rem',
  },
  th: {
    padding: '0.6rem 0.75rem',
    textAlign: 'left' as const,
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    fontWeight: 600,
    color: '#374151',
    whiteSpace: 'nowrap' as const,
  },
  td: {
    padding: '0.5rem 0.75rem',
    borderBottom: '1px solid #f3f4f6',
    color: '#4b5563',
    verticalAlign: 'top' as const,
  },
  badge: {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  expandableRow: {
    cursor: 'pointer',
  },
  errorRow: {
    backgroundColor: '#fef2f2',
  },
  errorMessage: {
    padding: '0.5rem 0.75rem 0.75rem 2.5rem',
    fontSize: '0.8rem',
    color: '#991b1b',
    fontStyle: 'italic' as const,
  },
  emptyRow: {
    textAlign: 'center' as const,
    padding: '2rem 1rem',
    color: '#6b7280',
    fontSize: '0.85rem',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 0',
    fontSize: '0.8rem',
    color: '#6b7280',
  },
  pageButton: {
    padding: '0.35rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#374151',
    cursor: 'pointer',
  },
  pageButtonDisabled: {
    padding: '0.35rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    backgroundColor: '#f9fafb',
    color: '#9ca3af',
    cursor: 'not-allowed',
  },
  paginationButtons: {
    display: 'flex',
    gap: '0.5rem',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '2rem',
    color: '#6b7280',
    fontSize: '0.85rem',
  },
  snapshotBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    fontSize: '0.9rem',
    borderRadius: '4px',
    lineHeight: 1,
    transition: 'background-color 0.15s',
  },
};

export function UploadHistory({ refreshTrigger }: UploadHistoryProps) {
  const [entries, setEntries] = useState<UploadHistoryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedUploadId, setSelectedUploadId] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchHistory = useCallback(async (pageNum: number) => {
    setIsLoading(true);
    try {
      const response = await apiClient<HistoryResponse>(
        `/order-import/history?page=${pageNum}&pageSize=${PAGE_SIZE}`,
      );
      setEntries(response.entries || []);
      setTotal(response.total);
    } catch {
      // Silently fail — table will show empty state
      setEntries([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory(page);
  }, [page, fetchHistory, refreshTrigger]);

  const handlePrev = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const toggleExpand = (entry: UploadHistoryEntry) => {
    if (entry.status === 'Failed' && entry.errorMessage) {
      setExpandedId(expandedId === entry.id ? null : entry.id);
    }
  };

  const handleSnapshotClick = (entry: UploadHistoryEntry) => {
    setSelectedUploadId(entry.id);
    setSelectedFileName(entry.fileName);
    setIsModalOpen(true);
  };

  const handlePrintRollbackReport = async (entry: UploadHistoryEntry) => {
    try {
      const report = await apiClient<{
        uploadId: string;
        fileName: string;
        fileReferenceId: string;
        uploadDate: string;
        status: string;
        summary: { totalOrders: number; totalTickets: number; totalGuests: number; totalSyntheticUsers: number };
        orders: Array<{ referenceId: string; purchaserName: string; purchaserEmail: string; tierName: string; ticketCount: number; tickets: Array<{ ticketNumber: number; guestName: string | null; tierName: string; status: string }> }>;
        guestRecords: Array<{ fullName: string; ticketNumber: string | null; chapterAffiliation: string | null; rsvpStatus: string; paymentStatus: string; linkedUserEmail: string | null }>;
      }>(`/order-import/${entry.id}/rollback-report`);

      const orderRows = report.orders.flatMap((o) =>
        o.tickets.map((t) =>
          `<tr><td>${o.referenceId}</td><td>${o.purchaserName}</td><td>${o.purchaserEmail}</td><td>${o.tierName}</td><td>TKT-${String(t.ticketNumber).padStart(3, '0')}</td><td>${t.guestName || '—'}</td><td>${t.status}</td></tr>`,
        ),
      ).join('');

      const guestRows = report.guestRecords.map((g) =>
        `<tr><td>${g.fullName}</td><td>${g.ticketNumber || '—'}</td><td>${g.chapterAffiliation || '—'}</td><td>${g.rsvpStatus}</td><td>${g.paymentStatus}</td><td>${g.linkedUserEmail || '—'}</td></tr>`,
      ).join('');

      const uploadDate = new Date(report.uploadDate).toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });

      const html = `<!DOCTYPE html><html><head>
        <title>Rollback Report — ${report.fileName}</title>
        <style>
          body { font-family: sans-serif; padding: 20px; font-size: 12px; }
          h1 { font-size: 16px; margin-bottom: 4px; }
          h2 { font-size: 13px; margin: 1.5rem 0 0.5rem 0; border-bottom: 1px solid #333; padding-bottom: 4px; }
          p { color: #666; font-size: 11px; margin: 2px 0; }
          .summary { display: flex; gap: 2rem; margin: 12px 0; }
          .summary-item { text-align: center; }
          .summary-item .value { font-size: 1.5rem; font-weight: 700; color: #d32f2f; }
          .summary-item .label { font-size: 10px; color: #666; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th { text-align: left; padding: 6px 8px; border-bottom: 2px solid #333; font-size: 11px; text-transform: uppercase; color: #555; }
          td { padding: 5px 8px; border-bottom: 1px solid #ddd; font-size: 11px; }
          .warning { background: #fef2f2; border: 1px solid #fca5a5; border-radius: 4px; padding: 10px 14px; margin: 12px 0; color: #991b1b; font-weight: 500; }
        </style>
      </head><body>
        <h1>Import Batch Rollback Report</h1>
        <p><strong>File:</strong> ${report.fileName} &nbsp;|&nbsp; <strong>Ref:</strong> ${report.fileReferenceId} &nbsp;|&nbsp; <strong>Uploaded:</strong> ${uploadDate}</p>
        <p><strong>Status:</strong> ${report.status} &nbsp;|&nbsp; <strong>Generated:</strong> ${new Date().toLocaleString()}</p>

        <div class="warning">⚠️ The following records will be permanently deleted if rollback is executed.</div>

        <div class="summary">
          <div class="summary-item"><div class="value">${report.summary.totalOrders}</div><div class="label">Orders</div></div>
          <div class="summary-item"><div class="value">${report.summary.totalTickets}</div><div class="label">Tickets</div></div>
          <div class="summary-item"><div class="value">${report.summary.totalGuests}</div><div class="label">Guests</div></div>
          <div class="summary-item"><div class="value">${report.summary.totalSyntheticUsers}</div><div class="label">User Accounts</div></div>
        </div>

        <h2>Orders &amp; Tickets (${report.summary.totalTickets})</h2>
        <table><thead><tr><th>Order Ref</th><th>Purchaser</th><th>Email</th><th>Tier</th><th>Ticket #</th><th>Guest Name</th><th>Status</th></tr></thead><tbody>${orderRows}</tbody></table>

        <h2>Guest Records (${report.summary.totalGuests})</h2>
        <table><thead><tr><th>Full Name</th><th>Ticket #</th><th>Chapter</th><th>RSVP</th><th>Payment</th><th>Linked Email</th></tr></thead><tbody>${guestRows}</tbody></table>
      </body></html>`;

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => printWindow.print(), 300);
      }
    } catch {
      alert('Failed to generate rollback report. You may not have Admin access.');
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedUploadId(null);
    setSelectedFileName('');
  };

  const formatUploadDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getUploaderName = (entry: UploadHistoryEntry): string => {
    if (entry.uploadedBy) {
      return entry.uploadedBy.fullName;
    }
    return '—';
  };

  const renderBadge = (status: string) => {
    const colors = statusColors[status] || { bg: '#f3f4f6', text: '#4b5563' };
    return (
      <span
        style={{
          ...styles.badge,
          backgroundColor: colors.bg,
          color: colors.text,
        }}
      >
        {status}
      </span>
    );
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={9} style={styles.loadingContainer}>
            Loading history...
          </td>
        </tr>
      );
    }

    if (entries.length === 0) {
      return (
        <tr>
          <td colSpan={9} style={styles.emptyRow}>
            No import history available
          </td>
        </tr>
      );
    }

    const rows: React.ReactNode[] = [];
    entries.forEach((entry) => {
      const isExpandable = entry.status === 'Failed' && !!entry.errorMessage;
      const isExpanded = expandedId === entry.id;

      rows.push(
        <tr
          key={entry.id}
          style={isExpandable ? styles.expandableRow : undefined}
          onClick={() => toggleExpand(entry)}
          title={isExpandable ? 'Click to view error details' : undefined}
        >
          <td style={styles.td}>
            {isExpandable && (
              <span style={{ marginRight: '0.35rem' }}>{isExpanded ? '▼' : '▶'}</span>
            )}
            {entry.fileName}
          </td>
          <td style={styles.td}>{entry.fileReferenceId}</td>
          <td style={styles.td}>{formatUploadDate(entry.uploadDate)}</td>
          <td style={styles.td}>{entry.guestCount}</td>
          <td style={styles.td}>{entry.orderCount}</td>
          <td style={styles.td}>{getUploaderName(entry)}</td>
          <td style={styles.td}>{entry.duplicateCount}</td>
          <td style={styles.td}>{renderBadge(entry.status)}</td>
          <td style={styles.td}>
            {entry.hasSnapshots && (
              <button
                style={styles.snapshotBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSnapshotClick(entry);
                }}
                aria-label={`View snapshots for ${entry.fileName}`}
                title="View snapshots"
                type="button"
              >
                📸
              </button>
            )}
            {entry.status === 'Confirmed' && entry.hasSnapshots && (
              <button
                style={styles.snapshotBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrintRollbackReport(entry);
                }}
                aria-label={`Print rollback report for ${entry.fileName}`}
                title="Print rollback report"
                type="button"
              >
                🖨️
              </button>
            )}
          </td>
        </tr>,
      );

      if (isExpanded) {
        rows.push(
          <tr key={`${entry.id}-error`} style={styles.errorRow}>
            <td colSpan={9} style={styles.errorMessage}>
              {entry.errorMessage}
            </td>
          </tr>,
        );
      }
    });

    return <>{rows}</>;
  };

  return (
    <div style={styles.container}>
      <h4 style={styles.heading}>Upload History</h4>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>File Name</th>
              <th style={styles.th}>Reference ID</th>
              <th style={styles.th}>Upload Date</th>
              <th style={styles.th}>Records</th>
              <th style={styles.th}>Orders</th>
              <th style={styles.th}>Uploaded By</th>
              <th style={styles.th}>Duplicates</th>
              <th style={styles.th}>Status</th>
              <th style={{ ...styles.th, width: '40px' }}></th>
            </tr>
          </thead>
          <tbody>{renderTableBody()}</tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div style={styles.pagination}>
          <span>
            Page {page} of {totalPages} ({total} total)
          </span>
          <div style={styles.paginationButtons}>
            <button
              style={page <= 1 ? styles.pageButtonDisabled : styles.pageButton}
              onClick={handlePrev}
              disabled={page <= 1}
            >
              Prev
            </button>
            <button
              style={page >= totalPages ? styles.pageButtonDisabled : styles.pageButton}
              onClick={handleNext}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {isModalOpen && selectedUploadId && (
        <SnapshotViewerModal
          uploadId={selectedUploadId}
          fileName={selectedFileName}
          isOpen={isModalOpen}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}
